import React, { useState, useRef } from 'react';
import { ChevronDown, ChevronUp, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import { SYMBOL_GROUPS } from '../mock';
import { t } from '../i18n';
import { useQuiz } from '../context/QuizContext';
import LinkPromptDialog from './LinkPromptDialog';

/**
 * SymbolToolbar - renders horizontal rows of math/science shortcut buttons.
 * Clicking a symbol inserts it into the currently-focused textarea/input on the page
 * (tracked via lastFocusedRef in a global window property).
 */
const SymbolToolbar = () => {
  const [open, setOpen] = useState(true);
  const [linkPrompt, setLinkPrompt] = useState(null); // 'image' | 'video' | null
  const { lang } = useQuiz();

  const insertToFocused = (text) => {
    const el = window.__mgLastFocused;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end   = el.selectionEnd ?? el.value.length;
    const newVal = el.value.substring(0, start) + text + el.value.substring(end);
    // Trigger native input event so React state updates via onChange handler
    const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set
                 || Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    setter.call(el, newVal);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    // Restore cursor
    const pos = start + text.length;
    setTimeout(() => { try { el.focus(); el.setSelectionRange(pos, pos); } catch(e) {} }, 0);
  };

  const handleMediaSubmit = (url) => {
    const html = linkPrompt === 'image' ? `<img src="${url}" alt="image" />` : `<video controls src="${url}"></video>`;
    insertToFocused(html);
  };

  return (
    <div className="mg-symbols-wrap">
      <div className="mg-symbols-head">
        <button className="mg-sym-btn wide" onClick={() => setOpen(o => !o)} title="Toggle" aria-label="toggle-symbols">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>
      {open && (
        <div className="mg-symbols-body">
          {SYMBOL_GROUPS.map((row, i) => (
            <div key={i} className="mg-symbols-row">
              {row.map((sym, j) => (
                <button key={`${i}-${j}`}
                        className="mg-sym-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertToFocused(sym + ' ')}
                        title={sym}>
                  {sym}
                </button>
              ))}
            </div>
          ))}
          <div className="mg-symbols-row" style={{ marginTop: 4 }}>
            <button className="mg-sym-btn wide" onMouseDown={e => e.preventDefault()} onClick={() => setLinkPrompt('image')}>
              <ImageIcon size={12} style={{ marginRight: 4 }} /> {t('insertImage', lang)}
            </button>
            <button className="mg-sym-btn wide" onMouseDown={e => e.preventDefault()} onClick={() => setLinkPrompt('video')}>
              <VideoIcon size={12} style={{ marginRight: 4 }} /> {t('insertVideo', lang)}
            </button>
          </div>
        </div>
      )}

      <LinkPromptDialog
        open={linkPrompt !== null}
        onOpenChange={(v) => { if (!v) setLinkPrompt(null); }}
        title={linkPrompt === 'video' ? t('insertVideoTitle', lang) : t('insertImageTitle', lang)}
        placeholder={linkPrompt === 'video' ? t('insertVideoPh', lang) : t('insertImagePh', lang)}
        confirmLabel={t('insertAction', lang)}
        cancelLabel={t('cancel', lang)}
        onSubmit={handleMediaSubmit}
      />
    </div>
  );
};

export default SymbolToolbar;
